import React, { useState, useMemo, useContext } from "react";
import CrosstabModal from "../DashboardBuilder/CrosstabModal";
import "../styles/SidebarDashboard.css";
import FetchingFreq from "../../contexts/FetchingFreq";
import { useToast } from "../../contexts/ToastContext";

export default function Sidebar({
  dataGroup,
  selectedQuestionKey,
  setSelectedQuestionKey,
  handleAddKPI,
  handleAddFilter,
  handleAddGlobalFilter,
  globalFilters = [],
  selectedTab,
  selectedVariable,
  crosstabDisplayData,
  setCrosstabDisplayData,
  handleAddGlobalBanner,
  handleAddTabBanner,
  globalBanners = [],
  handleRemoveGlobalBanner,
}) {
  const { handleTitleClick } = useContext(FetchingFreq);
  const { toast } = useToast();
  const [searchTerm, setSearchTerm] = useState("");
  const [multiSelected, setMultiSelected] = useState([]);
  const [showCrosstabModal, setShowCrosstabModal] = useState(false);

  const sortedEntries = Object.entries(dataGroup || {}).sort(([, a], [, b]) => {
    const orderA = a?.order !== undefined ? Number(a.order) : (a?._order !== undefined ? Number(a._order) : Infinity);
    const orderB = b?.order !== undefined ? Number(b.order) : (b?._order !== undefined ? Number(b._order) : Infinity);
    const validA = isNaN(orderA) ? Infinity : orderA;
    const validB = isNaN(orderB) ? Infinity : orderB;
    return validA - validB;
  });

  const filteredEntries = useMemo(
    () =>
      sortedEntries.filter(([key, entry]) =>
        entry != null &&
        (entry.title || key).toLowerCase().includes(searchTerm.toLowerCase())
      ),
    [searchTerm, sortedEntries]
  );

  const handleAddKPIClick = () => {
    if (multiSelected.length === 0) {
      toast.warning("Please select at least one variable.");
      return;
    }
    handleAddKPI(multiSelected);
    setMultiSelected([]);
    setSelectedQuestionKey("");
  };

  const handleLocalFilterClick = () => {
    if (!selectedQuestionKey) {
      toast.warning("Please select a variable first.");
      return;
    }
    if (globalFilters.some((f) => f.variable_name === selectedQuestionKey)) {
      toast.warning("This variable is already added as a Global Filter.");
      return;
    }
    if (selectedTab?.filters?.some((f) => f.variable_name === selectedQuestionKey)) {
      toast.warning("This variable is already added as a Local Filter in this tab.");
      return;
    }
    handleAddFilter();
  };

  const handleGlobalFilterClick = () => {
    if (!selectedQuestionKey) {
      toast.warning("Please select a variable first.");
      return;
    }
    if (globalFilters.some((f) => f.variable_name === selectedQuestionKey)) {
      toast.warning("This variable is already added as a Global Filter.");
      return;
    }
    handleAddGlobalFilter();
  };

  return (
    <div className="sidebar">
      <div className="sidebar-header">
        <span className="sidebar-title">Variables</span>
      </div>

      {/* Action buttons */}
      <div className="sidebar-buttons">
        <button className="sb-btn sb-btn--kpi" onClick={handleAddKPIClick}>
          ➕ Add KPI{multiSelected.length > 0 ? ` (${multiSelected.length})` : ""}
        </button>
        <button className="sb-btn sb-btn--local" onClick={handleLocalFilterClick}>
          🎯 Local Filter
        </button>
        <button className="sb-btn sb-btn--global" onClick={handleGlobalFilterClick}>
          🌐 Global Filter
        </button>
        <button className="sb-btn sb-btn--tab-banner" onClick={handleAddTabBanner}>
          📊 Tab Banner
        </button>
        <button className="sb-btn sb-btn--global-banner" onClick={handleAddGlobalBanner}>
          🌍 Global Banner
        </button>
      </div>

      {/* Global banners list */}
      {globalBanners.length > 0 && (
        <div className="sidebar-banner-section">
          <span className="sidebar-banner-title">Global Banners</span>
          {globalBanners.map((b) => (
            <div key={b.key} className="sidebar-banner-chip">
              <span className="sidebar-banner-chip-label">{b.label || b.key}</span>
              <button
                className="sidebar-banner-chip-remove"
                onClick={() => handleRemoveGlobalBanner(b.key)}
              >
                ×
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Search */}
      <input
        type="text"
        className="sidebar-search"
        placeholder="Search variables..."
        value={searchTerm}
        onChange={(e) => setSearchTerm(e.target.value)}
      />

      {/* Selected variable info */}
      {selectedVariable && (
        <div className="selected-var-info">
          <strong>{selectedVariable.Qtitle || selectedVariable.title}</strong>
          <span className="selected-var-type">
            {selectedVariable.questionType || selectedVariable.type}
          </span>
        </div>
      )}

      {/* Variable list */}
      <div className="variable-section">
        {filteredEntries.length > 0 ? (
          filteredEntries.map(([key, entry]) => {
            const isLocalFilter = selectedTab?.filters?.some(
              (f) => f.variable_name === key
            );
            const isGlobalFilter = globalFilters.some(
              (f) => f.variable_name === key
            );
            const isSelected = multiSelected.includes(key);
            const isActive = selectedQuestionKey === key;

            return (
              <div
                key={key}
                className={[
                  "variable-item",
                  isSelected ? "multi-selected" : "",
                  isActive ? "selected" : "",
                ]
                  .filter(Boolean)
                  .join(" ")}
                onClick={() => {
                  if (entry?.removeTab) {
                    toast.warning(
                      `Table run is not allowed for "${entry.title || key}" — turn off "Remove Tab" for this object first.`
                    );
                    return;
                  }
                  if (isSelected) {
                    setMultiSelected(multiSelected.filter((i) => i !== key));
                  } else {
                    setMultiSelected([...multiSelected, key]);
                  }
                  setSelectedQuestionKey(key);
                  handleTitleClick(key);
                }}
                title={entry.title || key}
              >
                {entry.title || key}
              </div>
            );
          })
        ) : (
          <p className="no-data">No variables found</p>
        )}
      </div>

      {showCrosstabModal && (
        <CrosstabModal
          selectedVars={sortedEntries.map(([k]) => k)}
          onClose={() => setShowCrosstabModal(false)}
          onGenerate={() => setShowCrosstabModal(false)}
          crosstabDisplayData={crosstabDisplayData}
          setCrosstabDisplayData={setCrosstabDisplayData}
        />
      )}
    </div>
  );
}
